import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Building2 } from "lucide-react";

function renderAddButton(open, setOpen, form, setForm, save) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-dept-btn" className="bg-gold text-black hover:bg-gold-hover"><Plus className="w-4 h-4 mr-1" />Add department</Button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-display">New department</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="dept-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Color</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="bg-[#0A0A0A] border-white/10 h-10 w-24" /></div>
        </div>
        <DialogFooter><Button data-testid="save-dept-btn" onClick={save} className="bg-gold text-black hover:bg-gold-hover">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Departments({ embedded = false }) {
  const { user } = useAuth();
  const canEdit = user.role === "org_admin";
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", color: "#F59E0B" });

  const load = async () => {
    const [d, u] = await Promise.all([api.get("/departments"), api.get("/users")]);
    setItems(d.data);
    setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    await api.post("/departments", form);
    setOpen(false);
    setForm({ name: "", description: "", color: "#F59E0B" });
    load();
    toast.success("Department added");
  };
  const del = async (id) => {
    if (!confirm("Delete this department?")) return;
    await api.delete(`/departments/${id}`);
    load();
  };

  return (
    <div className={embedded ? "space-y-4" : "space-y-6 animate-fade-up"} data-testid="departments-page">
      {!embedded && (
        <header className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Structure</div>
            <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Production Departments</h1>
            <p className="mt-1 text-zinc-400 text-sm">Who does what on this show.</p>
          </div>
          {canEdit && renderAddButton(open, setOpen, form, setForm, save)}
        </header>
      )}
      {embedded && canEdit && (
        <div className="flex justify-end">{renderAddButton(open, setOpen, form, setForm, save)}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => {
          const members = users.filter((u) => u.department_id === d.id);
          return (
            <div key={d.id} className="rounded-lg border border-white/10 bg-[#121212] p-5 hover:border-white/20 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md grid place-items-center" style={{ backgroundColor: `${d.color}22`, color: d.color }}>
                    <Building2 className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-display text-lg">{d.name}</div>
                    <div className="text-xs text-zinc-500">{members.length} member{members.length === 1 ? "" : "s"}</div>
                  </div>
                </div>
                {canEdit && <button data-testid={`del-dept-${d.id}`} onClick={() => del(d.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <div className="mt-3 text-sm text-zinc-400">{d.description}</div>
              {members.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {members.map((m) => (
                    <span key={m.id} className="text-[11px] rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-zinc-300">{m.name}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <div className="col-span-full text-sm text-zinc-500 border border-dashed border-white/10 rounded-md p-8 text-center">No departments yet.</div>}
      </div>
    </div>
  );
}
